"use client";

import { Loader2, X } from "lucide-react";
import { AttachmentPreviewPanel } from "@/app/chat/components/message-displays/AttachmentPreviewPanel";
import { useChatMediaPreview } from "@/app/ai/hooks/useChatMediaPreview";

/**
 * Inner content of the AI source-document preview: resolves the batch's source
 * document (metadata + blob) and renders it through the shared
 * AttachmentPreviewPanel. Container-agnostic — both the legacy fixed overlay
 * (SourceDocumentSlideOver) and the resizable push panel
 * (SourceDocumentPanelProvider) wrap this same body so the preview/close logic
 * lives in one place.
 *
 * `open` gates the (potentially large) blob download — pass the panel's visible
 * state so a collapsed/animating-out panel doesn't eagerly fetch the .xlsx.
 */
export function SourceDocumentPanelBody({
  batchId,
  open,
  onClose,
}: {
  readonly batchId: string;
  readonly open: boolean;
  readonly onClose: () => void;
}) {
  const { source, doc, loading, error, getFileType } = useChatMediaPreview(
    batchId,
    open,
  );

  // Metadata still resolving (or no source): show a dismissible spinner so the
  // panel is never a dead end while the presign round-trips.
  if (!source) {
    return (
      <div className="relative h-full flex items-center justify-center">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-2 right-2 h-8 w-8 inline-flex items-center justify-center rounded-md text-gray-500 hover:bg-gray-100"
        >
          <X className="h-4 w-4" />
        </button>
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <AttachmentPreviewPanel
      attachmentName={source.name}
      getFileType={getFileType}
      documentPdfData={doc?.documentPdfData ?? null}
      documentArrayBuffer={doc?.documentArrayBuffer ?? null}
      documentBlobUrl={doc?.documentBlobUrl ?? null}
      htmlContent={doc?.htmlContent ?? null}
      textContent={null}
      previewLoading={loading}
      previewError={error}
      tableRows={[]}
      onClose={onClose}
      onDownload={() =>
        source.url && window.open(source.url, "_blank", "noopener,noreferrer")
      }
      isDownloading={false}
    />
  );
}
